import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { DocPresenceKey, DocViewer } from "@productlane/lib"
import { DOC_TYPING_TTL_MS } from "@productlane/lib"
import { queries } from "@productlane/zero/queries"

import type { DocPeers } from "@/lib/doc-presence-peers"
import { otherViewers, typingViewers } from "@/lib/doc-presence-peers"
import { joinDoc } from "@/lib/sockets/doc-presence"
import { useAuthStateData } from "@/lib/states/auth-state"
import { useDocPresenceState } from "@/lib/states/doc-presence-state"

const NO_PEERS: DocPeers = {}

// The socket carries what each tab's session had at login; the member list is
// what every other avatar in the app draws, so names and photos match it.
export interface MemberViewer extends DocViewer {
	email?: string
}

function useMemberViewers(viewers: DocViewer[]): MemberViewer[] {
	const [members] = useQuery(queries.teamMembers())
	return useMemo(
		() =>
			viewers.map((viewer) => {
				const user = members.find((m) => m.user?.id === viewer.userId)?.user
				return user
					? {
							userId: viewer.userId,
							name: user.name ?? viewer.name,
							imageUrl: user.imageUrl ?? viewer.imageUrl,
							email: user.email ?? undefined,
						}
					: viewer
			}),
		[viewers, members],
	)
}

/** Announces the reader in a doc while the calling screen is mounted. */
export function useJoinDoc(docKey: DocPresenceKey | undefined) {
	const session = useAuthStateData()
	const workspaceId = session?.workspaceId
	const userId = session?.userId
	const name = session?.name
	const imageUrl = session?.imageUrl

	useEffect(() => {
		if (!docKey || !workspaceId || !userId) return
		return joinDoc(docKey, workspaceId, {
			userId,
			name: name ?? "A teammate",
			imageUrl,
		})
	}, [docKey, workspaceId, userId, name, imageUrl])
}

const peersOf = (docKey: DocPresenceKey | undefined) =>
	docKey ? (useDocPresenceState.getState().docs[docKey] ?? NO_PEERS) : NO_PEERS

/** Teammates who have the doc open, without the reader. */
export function useDocViewers(
	docKey: DocPresenceKey | undefined,
): MemberViewer[] {
	const session = useAuthStateData()
	const selfUserId = session?.userId
	// A string, so a caret move or typing event that changes no viewer renders nothing.
	const signature = useDocPresenceState((state) =>
		otherViewers(
			docKey ? (state.docs[docKey] ?? NO_PEERS) : NO_PEERS,
			selfUserId,
		)
			.map((v) => `${v.userId}:${v.name}:${v.imageUrl ?? ""}`)
			.join("|"),
	)
	const viewers = useMemo(
		() => otherViewers(peersOf(docKey), selfUserId),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- recomputed when the signature changes
		[signature, docKey, selfUserId],
	)
	return useMemberViewers(viewers)
}

/** Teammates typing in the doc right now; re-checks while anyone is listed. */
export function useDocTyping(
	docKey: DocPresenceKey | undefined,
): MemberViewer[] {
	const session = useAuthStateData()
	const selfUserId = session?.userId
	// Who has a typing flag, ignoring heartbeats and caret moves; the tick below
	// drops anyone whose flag outlives the TTL.
	const signature = useDocPresenceState((state) =>
		Object.values(docKey ? (state.docs[docKey] ?? NO_PEERS) : NO_PEERS)
			.filter((peer) => peer.typingAt !== null)
			.map((peer) => peer.viewer.userId)
			.sort()
			.join("|"),
	)
	const [tick, setTick] = useState(0)

	useEffect(() => {
		if (!signature) return
		const timer = setInterval(
			() => setTick((value) => value + 1),
			DOC_TYPING_TTL_MS / 3,
		)
		return () => clearInterval(timer)
	}, [signature])

	const typing = useMemo(
		() => typingViewers(peersOf(docKey), selfUserId, Date.now()),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- recomputed on each signature change and tick
		[signature, tick, docKey, selfUserId],
	)

	return useMemberViewers(typing)
}

/** Looks up the seed a teammate's avatar uses (their name, as in the assignee picker), for colouring their caret. */
export function useColorSeedLookup(): (userId: string) => string {
	const [members] = useQuery(queries.teamMembers())
	const membersRef = useRef(members)
	membersRef.current = members
	const [lookup] = useState(
		() => (userId: string) =>
			membersRef.current.find((m) => m.user?.id === userId)?.user?.name ??
			userId,
	)
	return lookup
}

/** Every member's email, for telling a teammate apart from the customer on the chat socket. */
export function useMemberEmails(): { current: Set<string> } {
	const [members] = useQuery(queries.teamMembers())
	const emails = useRef(new Set<string>())
	emails.current = new Set(
		members.flatMap((m) => (m.user?.email ? [m.user.email.toLowerCase()] : [])),
	)
	return emails
}
